/**
 * Must match backend/utils/placeDimensions.js and placePalette.js (index 0 = transparent).
 * Order: neutrals → warm school colors → cool school colors.
 */
export const PLACE_WIDTH = 60;
export const PLACE_HEIGHT = 40;

export const PLACE_PALETTE_RGBA: readonly [number, number, number, number][] = [
  [0, 0, 0, 0],
  [251, 251, 251, 255],
  [12, 15, 11, 255],
  [29, 42, 52, 255],
  [135, 138, 148, 255],
  [180, 189, 201, 255],
  [158, 27, 50, 255],
  [93, 47, 56, 255],
  [224, 110, 29, 255],
  [244, 191, 24, 255],
  [214, 175, 136, 255],
  [114, 95, 77, 255],
  [12, 127, 11, 255],
  [16, 87, 159, 255],
  [17, 50, 101, 255],
  [86, 53, 132, 255],
];

export const PLACE_LABELS = [
  'Eraser',
  'White',
  'Black',
  'Charcoal',
  'Gray',
  'Silver',
  'Crimson',
  'Maroon',
  'Burnt orange',
  'Gold',
  'Tan',
  'Brown',
  'Green',
  'Royal blue',
  'Navy',
  'Purple',
] as const;

const CHROMA_PIX_NEUTRAL = 22;
const CHROMA_PAL_NEUTRAL = 18;

function rgbToHsv255(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d > 1e-8) {
    if (max === rn) {
      h = ((gn - bn) / d) * 60;
      if (h < 0) h += 360;
    } else if (max === gn) {
      h = ((bn - rn) / d + 2) * 60;
    } else {
      h = ((rn - gn) / d + 4) * 60;
    }
  }
  const s = max < 1e-8 ? 0 : d / max;
  const v = max;
  return { h, s, v };
}

function hueDeltaDeg(h1: number, h2: number): number {
  let d = Math.abs(h1 - h2);
  if (d > 180) d = 360 - d;
  return d;
}

const PLACE_PALETTE_HSV: ({ h: number; s: number; v: number } | null)[] = PLACE_PALETTE_RGBA.map((row, i) => {
  if (i === 0) return null;
  return rgbToHsv255(row[0], row[1], row[2]);
});

/** Same algorithm as backend/utils/placePalette.js rgbaToNearestIndex (hue-first when chroma allows). */
export function rgbaToNearestIndex(r: number, g: number, b: number, a: number): number {
  if (a < 90) return 0;
  const chromaPix = Math.max(r, g, b) - Math.min(r, g, b);
  const ph = rgbToHsv255(r, g, b);
  const useRgbOnly = chromaPix < CHROMA_PIX_NEUTRAL;

  let best = 1;
  let bestCost = Infinity;

  for (let i = 1; i < PLACE_PALETTE_RGBA.length; i++) {
    const [pr, pg, pb] = PLACE_PALETTE_RGBA[i];
    const rgbDist = (r - pr) ** 2 + (g - pg) ** 2 + (b - pb) ** 2;

    if (useRgbOnly) {
      if (rgbDist < bestCost) {
        bestCost = rgbDist;
        best = i;
      }
      continue;
    }

    const chromaPal = Math.max(pr, pg, pb) - Math.min(pr, pg, pb);
    if (chromaPal < CHROMA_PAL_NEUTRAL) {
      if (rgbDist < bestCost) {
        bestCost = rgbDist;
        best = i;
      }
      continue;
    }

    const palHsv = PLACE_PALETTE_HSV[i]!;
    const hueD = hueDeltaDeg(ph.h, palHsv.h) / 180;
    const cost =
      hueD * hueD * 10000 +
      (ph.s - palHsv.s) ** 2 * 80 +
      (ph.v - palHsv.v) ** 2 * 80 +
      rgbDist * 0.015;

    if (cost < bestCost) {
      bestCost = cost;
      best = i;
    }
  }
  return best;
}
